import { defineStore } from 'pinia';
import { notificationService } from '@/services/notification';
import type { Notification, NotificationResponse } from '@/types/notification';
import type { AxiosResponse } from 'axios';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  currentPage: number;
  pageSize: number;
  loading: boolean;
  total: number;
}

export const useNotificationStore = defineStore('notification', {
  state: (): NotificationState => ({
    notifications: [],
    unreadCount: 0,
    currentPage: 1,
    pageSize: 20,
    loading: false,
    total: 0
  }),

  actions: {
    async fetchNotifications(type: 'all' | 'read' | 'unread' = 'all') {
      this.loading = true;
      try {
        let response: AxiosResponse<NotificationResponse>;
        switch (type) {
          case 'read':
            response = await notificationService.getReadNotifications(this.currentPage, this.pageSize);
            break;
          case 'unread':
            response = await notificationService.getUnreadNotifications(this.currentPage, this.pageSize);
            break;
          default:
            response = await notificationService.getAllNotifications(this.currentPage, this.pageSize);
        }
        
        if (response.data.code === 200) {
          this.notifications = response.data.data.data || [];
          this.total = response.data.data.total;
          // 如果是获取未读通知，更新未读数量
          if (type === 'unread') {
            this.unreadCount = response.data.data.total;
          }
        }
      } catch (error) {
        console.error('获取通知失败:', error);
      } finally {
        this.loading = false;
      }
    },

    async markAllAsRead() {
      try {
        const response = await notificationService.markAllAsRead();
        if (response.data.code === 200) {
          this.notifications = this.notifications.map(notification => ({
            ...notification,
            isRead: true,
            readTime: new Date().toISOString()
          }));
          this.unreadCount = 0;
          // 重新获取通知列表
          await this.fetchNotifications();
        }
      } catch (error) {
        console.error('标记全部已读失败:', error);
      }
    },

    async markAsRead(id: number) {
      try {
        const response = await notificationService.markAsRead(id);
        if (response.data.code === 200) {
          const notification = this.notifications.find(n => n.id === id);
          if (notification && !notification.isRead) {
            notification.isRead = true;
            notification.readTime = new Date().toISOString();
            this.unreadCount--;
          }
          // 重新获取通知列表
          await this.fetchNotifications();
        }
      } catch (error) {
        console.error('标记已读失败:', error);
      }
    },

    setPage(page: number) {
      this.currentPage = page;
      this.fetchNotifications();
    },

    setPageSize(size: number) {
      this.pageSize = size;
      this.currentPage = 1; // 重置到第一页
      this.fetchNotifications();
    }
  }
}); 